import { FunctionComponent, useEffect, useState } from "react"
import { Button, Col, Form, Modal } from "react-bootstrap"
import { EntityPass, Pass } from "../data/models.interface"
import { formatDate } from "../utils/AccessControlFormatter"
import ItemSelect from "./ItemSelect"

interface IEntityPassModal {
	onHide: () => void
	entityPasses: EntityPass[]
	setEntityPasses: (p: EntityPass[]) => any
	index: number | null
	passes: Pass[]
}
const EntityPassModal: FunctionComponent<IEntityPassModal> = ({
	onHide,
	entityPasses,
	setEntityPasses,
	index,
	passes,
}) => {
	const [pass, setPass] = useState<Pass>()
	const [startDate, setStartDate] = useState("")
	const [endDate, setEndDate] = useState("")
	const [touched, setTouched] = useState<EntityPassTouched>({})
	// changing the index is what causes the Modal to show, so reload the fields at the same time
	useEffect(() => {
		if (index != null) {
			setPass(passes.find((d) => d.id === entityPasses?.[index]?.passId))
			setStartDate(formatDate(entityPasses?.[index]?.startDate))
			setEndDate(formatDate(entityPasses?.[index]?.endDate))
			setTouched({})
		}
	}, [entityPasses, index, passes])
	//
	const validationErrors = validate(pass, startDate, endDate)
	//
	const save = () => {
		if (index != null) {
			// the modal shouldn't be open if index is undefined
			// show errors on all fields
			setTouched({ pass: true, startDate: true, endDate: true })
			// don't save if there are errors
			if (Object.values(validationErrors).some((e) => !!e)) return
			//
			if (entityPasses == null) {
				entityPasses = [] // in case this is the first row
			} else {
				entityPasses = [...entityPasses] // make sure useState registers it as a new object
			}
			// add a new row if this is an add
			if (entityPasses.length <= index) entityPasses.push({} as EntityPass)
			// then we can edit the new or existing row the same way
			entityPasses[index].passId = pass?.id!
			entityPasses[index].passName = pass?.name!
			entityPasses[index].startDate = startDate || "2000-01-01"
			entityPasses[index].endDate = endDate || "2999-12-31"
			setEntityPasses(entityPasses) // update state
			onHide() // close the window
		}
	}
	const remove = () => {
		// the modal shouldn't be open if index is undefined
		// the remove button shouldn't be visible if entityPasses is undefined
		if (entityPasses && index != null) {
			entityPasses = [...entityPasses]
			entityPasses.splice(index, 1)
			setEntityPasses(entityPasses) // update state
			onHide() // close the window
		}
	}
	//
	return (
		<Modal show={index != null} onHide={onHide}>
			<Modal.Header closeButton={true}>
				<Modal.Title>Edit Pass</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form.Group className="mb-3" controlId="door">
					<Form.Label>Pass</Form.Label>
					<ItemSelect
						placeholder="--Select a Pass--"
						required
						valueId={pass?.id}
						onChange={setPass}
						onBlur={() => setTouched({ ...touched, pass: true })}
						items={passes}
						convert={(d) => ({ id: d.id, name: d.name })}
						isInvalid={touched.pass && validationErrors.pass != null}
					/>
					<Form.Control.Feedback type="invalid">{validationErrors.pass}</Form.Control.Feedback>
				</Form.Group>
				<Form.Group className="mb-3" controlId="startDate">
					<Form.Label>Start Date</Form.Label>
					<Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
				</Form.Group>
				<Form.Group className="mb-3" controlId="endDate">
					<Form.Label>End Date</Form.Label>
					<Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
				</Form.Group>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" disabled={false} onClick={onHide}>
					Cancel
				</Button>
				<Button variant="danger" hidden={index != null && index >= (entityPasses?.length || 0)} onClick={remove}>
					Remove
				</Button>
				<Col></Col>
				<Button variant="primary" disabled={false} onClick={save}>
					OK
				</Button>
			</Modal.Footer>
		</Modal>
	)
}
export default EntityPassModal

const validate = (pass?: Pass, startDate?: string, endDate?: string) => {
	const validationErrors: EntityPassErrors = {}
	if (!pass) validationErrors.pass = "Pass is required."
	return validationErrors
}

interface EntityPassTouched {
	pass?: boolean
	startDate?: boolean
	endDate?: boolean
}
interface EntityPassErrors {
	pass?: string
	startDate?: string
	endDate?: string
}
